/// <reference types="vite/client" />

import { z } from 'zod'

const emptyStringToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value

const optionalUrl = z.preprocess(
  emptyStringToUndefined,
  z.string().url().optional(),
)

const optionalLogLevel = z.preprocess(
  emptyStringToUndefined,
  z.enum(['debug', 'info', 'warn', 'error', 'silent']).optional(),
)

const envSchema = z.object({
  VITE_API_URL: optionalUrl,
  VITE_APP_URL: optionalUrl,
  VITE_ADMIN_APP_URL: optionalUrl,
  VITE_COOKIE_DOMAIN: z.string().optional(),
  VITE_APP_NAME: z.string().optional(),
  VITE_LOG_LEVEL: optionalLogLevel,
  VITE_EXTENSION_ID: z.preprocess(
    emptyStringToUndefined,
    z.string().optional(),
  ),
  DEV: z.boolean(),
  PROD: z.boolean(),
  MODE: z.string(),
})

export function parseClientEnv(rawEnv: ImportMetaEnv) {
  const parsed = envSchema.safeParse(rawEnv)

  if (!parsed.success) {
    const message = parsed.error.errors.map(err => err.message).join('\n')
    throw new Error(`Invalid client environment configuration:\n${message}`)
  }

  if (parsed.data.PROD) {
    if (!parsed.data.VITE_API_URL) {
      throw new Error('VITE_API_URL is required in production builds.')
    }
    if (!parsed.data.VITE_APP_URL) {
      throw new Error('VITE_APP_URL is required in production builds.')
    }
    if (!parsed.data.VITE_ADMIN_APP_URL) {
      throw new Error('VITE_ADMIN_APP_URL is required in production builds.')
    }
  }

  return {
    apiUrl: parsed.data.VITE_API_URL ?? 'http://localhost:4005',
    appUrl: parsed.data.VITE_APP_URL ?? 'http://localhost:3005',
    adminAppUrl: parsed.data.VITE_ADMIN_APP_URL ?? 'http://localhost:4006',
    cookieDomain: parsed.data.VITE_COOKIE_DOMAIN ?? 'localhost',
    appName: parsed.data.VITE_APP_NAME,
    logLevel: parsed.data.VITE_LOG_LEVEL,
    // SOP capture browser extension. Comma-separated candidate IDs, tried in
    // order by the Connect card: store-distributed builds get a
    // store-assigned ID, while dev/unpacked builds are pinned by the manifest
    // `key` in apps/extension/wxt.config.ts. Put the store ID first once
    // published; the pinned dev ID stays as the default/fallback.
    extensionIds: (
      parsed.data.VITE_EXTENSION_ID ??
      'dnipfdclppajipekfgcldekfakgpfnjl,mhjkplajhlkjjolppalegfhlhbleaddm'
    )
      .split(',')
      .map(id => id.trim())
      .filter(Boolean),
    isDevelopment: parsed.data.DEV,
    isProduction: parsed.data.PROD,
  }
}

export const env = parseClientEnv(import.meta.env)

/** Chrome Web Store listing for the Scrambl Capture extension. */
export const EXTENSION_STORE_URL =
  'https://chromewebstore.google.com/detail/scrambl-capture/dnipfdclppajipekfgcldekfakgpfnjl'
